<template>
  <div id="app">
    <router-view v-if="isLoginRoute" />
    <div v-else class="layout">
      <div class="sidebar" :style="{ width: sidebarWidth }">
        <div class="logo">
          <span class="logo-icon"><img src="/image/logo256x256.png" alt="logo" class="sidebar-logo"></span>
          <span class="logo-text" v-show="!isCollapse">司海广告数据平台</span>
        </div>
        <el-menu
          :default-active="$route.path"
          router
          background-color="#001529"
          text-color="rgba(255, 255, 255, 0.65)"
          active-text-color="#fff"
          :collapse="isCollapse"
          :collapse-transition="false"
          :default-openeds="defaultOpeneds"
          unique-opened>
          <template v-for="node in menuTree">
            <el-submenu v-if="node.children && node.children.length" :key="'s-' + node.id" :index="'sub-' + node.id">
              <template slot="title">
                <i class="el-icon-setting"></i>
                <span slot="title">{{ translateMenu(node.name) }}</span>
              </template>
              <el-menu-item
                v-for="child in node.children"
                :key="'m-' + child.id"
                :index="child.path || ''">
                <i class="el-icon-document"></i>
                <span slot="title">{{ translateMenu(child.name) }}</span>
              </el-menu-item>
            </el-submenu>
            <el-menu-item v-else :key="'m-' + node.id" :index="node.path || ''">
              <i class="el-icon-s-home"></i>
              <span slot="title">{{ translateMenu(node.name) }}</span>
            </el-menu-item>
          </template>
        </el-menu>
      </div>
      <div class="main-container" :style="{ marginLeft: sidebarWidth }">
        <div class="header">
          <div class="header-left">
            <div class="collapse-btn" @click="isCollapse = !isCollapse">
              <i :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
            </div>
            <el-breadcrumb separator="/">
              <el-breadcrumb-item :to="{ path: '/' }">
                <i class="el-icon-s-home"></i> {{ $t('app.home') }}
              </el-breadcrumb-item>
              <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index">
                {{ translateMenu(item.name) }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          <div class="header-right">
            <lang-switch />
            <div class="user-dropdown">
              <el-dropdown trigger="click">
                <span class="user-info">
                  <el-avatar :size="32" icon="el-icon-user-solid"></el-avatar>
                  <span class="username">{{ currentUserDisplay }}</span>
                  <i class="el-icon-arrow-down"></i>
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item icon="el-icon-user">{{ $t('app.profile') }}</el-dropdown-item>
                  <el-dropdown-item icon="el-icon-setting">{{ $t('app.settings') }}</el-dropdown-item>
                  <el-dropdown-item divided icon="el-icon-switch-button" @click.native="logout">
                    {{ $t('app.logout') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
        </div>
        <div class="tabs-bar">
          <div class="tabs-scroll">
            <div
              v-for="tab in visitedTabs"
              :key="tab.path"
              class="tab-item"
              :class="{ active: tab.path === activeTabPath }"
              @click="handleTabClick(tab.path)"
              @contextmenu.prevent="openContextMenu($event, tab)">
              <span class="tab-title" :title="tab.title">{{ tab.title }}</span>
              <i
                v-if="tab.path !== '/dashboard'"
                class="el-icon-close tab-close"
                @click.stop="handleTabRemove(tab.path)"></i>
            </div>
          </div>
        </div>
        <!-- Tab 右键菜单 -->
        <ul
          v-show="ctxMenu.visible"
          class="tab-ctx-menu"
          :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }">
          <li @click="ctxCloseCurrent">{{ $t('app.tabCloseCurrent') }}</li>
          <li @click="ctxCloseLeft">{{ $t('app.tabCloseLeft') }}</li>
          <li @click="ctxCloseRight">{{ $t('app.tabCloseRight') }}</li>
          <li @click="ctxCloseOthers">{{ $t('app.tabCloseOthers') }}</li>
          <li @click="ctxCloseAll">{{ $t('app.tabCloseAll') }}</li>
        </ul>
        <div class="main">
          <transition name="fade">
            <!-- menuTree 异步加载：v-hasPermi 在 inserted 时若 menuTree 仍为空会永久隐藏按钮，需 key 在菜单就绪后强制重挂载 -->
            <router-view :key="menuViewKey + '-' + $route.fullPath" />
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { logout, me } from '@/api/system/auth';
import { getMyMenus } from '@/api/system/menu';
import LangSwitch from '@/components/LangSwitch.vue';

export default {
  components: { LangSwitch },
  data() {
    return {
      menuTree: [],
      /** 菜单接口返回后递增，使 router-view 重挂载，令 v-hasPermi 在 menuTree 已填充时重新计算 */
      menuViewKey: 0,
      currentUser: null,
      isCollapse: false,
      defaultOpeneds: ['sub-2'], // 默认展开系统管理
      visitedTabs: [],
      ctxMenu: { visible: false, x: 0, y: 0, tab: null },
      // path → 菜单节点路径的缓存，避免每次路由跳转都递归遍历 menuTree（勿用 _ 前缀，Vue 2 不代理此类属性）
      menuTraceCache: {}
    };
  },
  computed: {
    sidebarWidth() {
      return this.isCollapse ? '64px' : '220px';
    },
    isLoginRoute() {
      return this.$route.path === '/login';
    },
    currentUserDisplay() {
      if (!this.currentUser) return this.$t('app.user');
      return this.currentUser.nickname || this.currentUser.username || this.$t('app.user');
    },
    activeTabPath() {
      return this.$route.path;
    },
    breadcrumbs() {
      const currentPath = this.$route.path;
      if (currentPath === '/' || currentPath === '/dashboard') {
        return [];
      }
      return this.resolveMenuTrace(currentPath);
    }
  },
  mounted() {
    if (!this.isLoginRoute) {
      this.fetchMyMenus();
      this.fetchCurrentUser();
      this.ensureVisitedTab(this.$route);
    }
    document.addEventListener('click', this.closeContextMenu);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeContextMenu);
  },
  watch: {
    $route(to) {
      if (to.path !== '/login') {
        if (this.menuTree.length === 0) {
          this.fetchMyMenus();
        }
        if (!this.currentUser) {
          this.fetchCurrentUser();
        }
        this.ensureVisitedTab(to);
      }
    },
    '$i18n.locale'() {
      this.refreshTabTitles();
    }
  },
  methods: {
    /** 翻译菜单名 — 使用 app.menu 中的映射 */
    translateMenu(name) {
      if (!name) return '';
      const key = 'app.menu.' + name;
      const translated = this.$t(key);
      // 如果翻译结果就是 key 本身，说明没找到翻译，返回原名
      return translated === key ? name : translated;
    },
    normalizePath(path) {
      return (path || '').toString().replace(/^\//, '');
    },
    resolveMenuTrace(path) {
      // 命中缓存直接返回，避免重复遍历
      if (this.menuTraceCache[path]) {
        return this.menuTraceCache[path];
      }
      const targetPathNorm = this.normalizePath(path);
      const findPath = (nodes, targetNorm, currentTrace = []) => {
        if (!nodes || !nodes.length) return null;
        for (const node of nodes) {
          const trace = [...currentTrace, node];
          if (node.path && this.normalizePath(node.path) === targetNorm) {
            return trace;
          }
          if (node.children && node.children.length) {
            const found = findPath(node.children, targetNorm, trace);
            if (found) return found;
          }
        }
        return null;
      };
      const pathTrace = findPath(this.menuTree, targetPathNorm) || [];
      this.menuTraceCache[path] = pathTrace;
      return pathTrace;
    },
    resolveMenuName(path, route) {
      const staticTitleMap = {
        '/dashboard': this.$t('app.home')
      };
      if (staticTitleMap[path]) {
        return staticTitleMap[path];
      }
      const trace = this.resolveMenuTrace(path);
      if (trace && trace.length) {
        return this.translateMenu(trace[trace.length - 1].name);
      }
      if (route && route.meta && route.meta.title) {
        return this.translateMenu(route.meta.title);
      }
      if (route && route.name) {
        return route.name;
      }
      return path || this.$t('app.unknownPage');
    },
    ensureVisitedTab(route) {
      if (!route || route.path === '/login') return;
      const existing = this.visitedTabs.find(tab => tab.path === route.path);
      const title = this.resolveMenuName(route.path, route);
      if (existing) {
        if (title && existing.title !== title) {
          existing.title = title;
        }
        return;
      }
      this.visitedTabs.push({
        path: route.path,
        title
      });
    },
    refreshTabTitles() {
      if (!this.visitedTabs.length) return;
      this.visitedTabs = this.visitedTabs.map(tab => {
        const title = this.resolveMenuName(tab.path);
        return {
          ...tab,
          title: title || tab.title
        };
      });
    },
    handleTabClick(targetPath) {
      if (targetPath && targetPath !== this.$route.path) {
        this.$router.push(targetPath);
      }
    },
    handleTabRemove(targetPath) {
      const index = this.visitedTabs.findIndex(tab => tab.path === targetPath);
      if (index === -1) return;
      const isActive = targetPath === this.$route.path;
      this.visitedTabs.splice(index, 1);
      if (isActive) {
        const fallback = this.visitedTabs[index] || this.visitedTabs[index - 1];
        this.$router.push(fallback ? fallback.path : '/dashboard');
      }
    },
    // ====== Tab 右键菜单 ======
    openContextMenu(e, tab) {
      this.ctxMenu = { visible: true, x: e.clientX, y: e.clientY, tab };
    },
    closeContextMenu() {
      this.ctxMenu.visible = false;
    },
    ctxCloseCurrent() {
      if (this.ctxMenu.tab) {
        this.handleTabRemove(this.ctxMenu.tab.path);
      }
      this.closeContextMenu();
    },
    ctxCloseLeft() {
      const idx = this.visitedTabs.findIndex(t => t.path === this.ctxMenu.tab.path);
      if (idx > 0) {
        this.visitedTabs = this.visitedTabs.filter((t, i) => i >= idx || t.path === '/dashboard');
        if (!this.visitedTabs.find(t => t.path === this.$route.path)) {
          this.$router.push(this.ctxMenu.tab.path);
        }
      }
      this.closeContextMenu();
    },
    ctxCloseRight() {
      const idx = this.visitedTabs.findIndex(t => t.path === this.ctxMenu.tab.path);
      if (idx < this.visitedTabs.length - 1) {
        this.visitedTabs = this.visitedTabs.filter((t, i) => i <= idx || t.path === '/dashboard');
        if (!this.visitedTabs.find(t => t.path === this.$route.path)) {
          this.$router.push(this.ctxMenu.tab.path);
        }
      }
      this.closeContextMenu();
    },
    ctxCloseOthers() {
      this.visitedTabs = this.visitedTabs.filter(t => t.path === this.ctxMenu.tab.path || t.path === '/dashboard');
      if (!this.visitedTabs.find(t => t.path === this.$route.path)) {
        this.$router.push(this.ctxMenu.tab.path);
      }
      this.closeContextMenu();
    },
    ctxCloseAll() {
      const dashboard = this.visitedTabs.find(t => t.path === '/dashboard');
      this.visitedTabs = dashboard ? [dashboard] : [];
      if (this.$route.path !== '/dashboard') {
        this.$router.push('/dashboard');
      }
      this.closeContextMenu();
    },
    /** 根据当前路由路径，计算需要默认展开的父级菜单 */
    calcDefaultOpeneds(menuTree, currentPath) {
      if (!menuTree || !menuTree.length || !currentPath) return [];
      const pathNorm = this.normalizePath(currentPath);
      const opened = [];
      for (const node of menuTree) {
        if (node.children && node.children.length) {
          const match = node.children.some(child => {
            return child.path && this.normalizePath(child.path) === pathNorm;
          });
          if (match) {
            opened.push('sub-' + node.id);
          }
        }
      }
      return opened;
    },
    async fetchMyMenus() {
      try {
        const res = await getMyMenus();
        let list = [];
        if (Array.isArray(res)) {
          list = res;
        } else if (res && Array.isArray(res.data)) {
          list = res.data;
        } else if (res && res.data && Array.isArray(res.data.records)) {
          list = res.data.records;
        }
        this.menuTree = list;
        if (list.length > 0) {
          this.menuViewKey += 1;
        }
        // 菜单数据更新后清空路径缓存
        this.menuTraceCache = {};
        // 只展开当前路由所在的父级菜单
        this.defaultOpeneds = this.calcDefaultOpeneds(list, this.$route.path);
        this.refreshTabTitles();
        this.ensureVisitedTab(this.$route);
      } catch (e) {
        console.error('[fetchMyMenus] error:', e);
        this.menuTree = [];
      }
    },
    async logout() {
      try {
        await logout();
      } finally {
        localStorage.removeItem('token');
        this.menuTree = [];
        this.menuViewKey = 0;
        this.currentUser = null;
        this.$router.replace('/login');
      }
    },
    async fetchCurrentUser() {
      try {
        const res = await me();
        if (res && res.data) {
          this.currentUser = res.data;
        } else if (res) {
          this.currentUser = res;
        }
      } catch (e) {
        console.warn('Failed to fetch current user', e);
        this.currentUser = null;
      }
    }
  }
};
</script>

<style>
/* ===== 全局布局 ===== */
#app {
  min-height: 100vh;
}

.layout {
  min-height: 100vh;
}

/* ===== 侧边栏（固定定位，脱离文档流） ===== */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 1001;
  background: linear-gradient(180deg, #001529 0%, #002140 100%);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  overflow-x: hidden;
  transition: width 0.28s ease;
}

.logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo-icon {
  /* font-size: 28px; */
  margin-right: 8px;
}

.logo-icon .sidebar-logo {
  width: 32px;
  height: 32px;
}

.logo-text {
  color: #fff;
  /* font-size: 20px; */
  font-weight: 700;
  letter-spacing: 2px;
  background: linear-gradient(135deg, #1890ff 0%, #36cfc9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 32px;
}

/* 菜单样式 */
.sidebar .el-menu {
  border-right: none !important;
}

.sidebar .el-menu-item {
  height: 50px !important;
  line-height: 50px !important;
  padding-left: 24px !important;
  transition: background-color 0.2s, color 0.2s;
  will-change: background-color;
  contain: layout style;
}

.sidebar .el-menu-item i {
  margin-right: 10px;
  font-size: 16px;
}

.sidebar .el-menu-item:hover {
  background-color: rgba(24, 144, 255, 0.15) !important;
}

.sidebar .el-menu-item.is-active {
  background-color: rgba(24, 144, 255, 0.1) !important;
  color: #1890ff !important;
  border-right: 3px solid #1890ff;
}

.sidebar .el-submenu__title {
  height: 50px !important;
  line-height: 50px !important;
  padding-left: 24px !important;
  transition: background-color 0.2s, color 0.2s;
  will-change: background-color;
  contain: layout style;
}

.sidebar .el-submenu__title i {
  margin-right: 10px;
  font-size: 16px;
}

.sidebar .el-submenu__title:hover {
  background-color: rgba(24, 144, 255, 0.15) !important;
}

.sidebar .el-submenu .el-menu-item {
  background-color: #000c17 !important;
  padding-left: 48px !important;
  min-width: 100%;
}

.sidebar .el-submenu .el-menu-item:hover {
  background-color: rgba(24, 144, 255, 0.2) !important;
}

.sidebar .el-submenu .el-menu-item.is-active {
  background-color: rgba(24, 144, 255, 0.15) !important;
  color: #1890ff !important;
}

/* ===== 主容器（通过 margin-left 让出侧边栏空间） ===== */
.main-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.28s ease;
  overflow: hidden;
}

/* ===== 顶部栏 ===== */
.header {
  background: #fff;
  padding: 0 24px;
  height: 64px;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  border-bottom: 1px solid #d8dce5;
  z-index: 10;
}

.header-left {
  display: flex;
  align-items: center;
}

.collapse-btn {
  padding: 0 15px;
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 20px;
  color: #595959;
  transition: all 0.3s;
  margin-right: 15px;
  margin-left: -24px;
}

.collapse-btn:hover {
  background-color: rgba(24, 144, 255, 0.05);
  color: #1890ff;
}

.header-right {
  display: flex;
  align-items: center;
}

/* ===== 顶部 Tab 栏 ===== */
.tabs-bar {
  background: #fff;
  padding: 6px 16px;
  border-bottom: 1px solid #ebeef5;
}

.tabs-scroll {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;
  scrollbar-width: none;
}

.tabs-scroll::-webkit-scrollbar {
  height: 0;
}

.tab-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #e6ebf5;
  background: #f7f9fc;
  color: #606266;
  cursor: pointer;
  user-select: none;
  transition: all 0.18s ease;
  flex-shrink: 0;
}

.tab-item:hover {
  color: #409eff;
  border-color: #b3d8ff;
  background: #ecf5ff;
}

.tab-item.active {
  color: #1890ff;
  border-color: #91caff;
  background: #e6f0ff;
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.12);
}

.tab-title {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

.tab-close {
  font-size: 12px;
  color: #909399;
  border-radius: 50%;
  padding: 2px;
  transition: all 0.15s ease;
}

.tab-close:hover {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.12);
}

/* ===== Tab 右键菜单 ===== */
.tab-ctx-menu {
  position: fixed;
  z-index: 3000;
  list-style: none;
  margin: 0;
  padding: 6px 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
  min-width: 140px;
}
.tab-ctx-menu li {
  padding: 8px 20px;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  transition: all 0.15s;
}
.tab-ctx-menu li:hover {
  background: #ecf5ff;
  color: #409eff;
}

/* 面包屑 */
.el-breadcrumb {
  font-size: 14px;
}

.el-breadcrumb__item:last-child .el-breadcrumb__inner {
  color: #1890ff !important;
  font-weight: 500;
}

/* 用户信息 */
.user-dropdown {
  cursor: pointer;
}

.user-info {
  display: flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 24px;
  transition: all 0.3s;
}

.user-info:hover {
  background-color: rgba(24, 144, 255, 0.1);
}

.username {
  margin: 0 8px;
  font-size: 14px;
  color: #595959;
}

.user-info .el-icon-arrow-down {
  font-size: 12px;
  color: #8c8c8c;
}

/* ===== 主内容区 ===== */
.main {
  padding: 0;
  background-color: #f0f2f5;
  flex: 1;
  overflow: auto;
}

/* 页面切换动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.12s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
